import {
  AddressSearch, CheckboxInput, Errors, Flex, FlexColumn, FlexGrow,
  ModelType,
  RadioListInput,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useMemo } from 'react';
import { CreditAddress, useCreditContext } from '../../../../contexts/credit';
import { StaticDataItem } from '../../../../api/useAccountApi';
import {
  Address, RentalIncomeAndExpenditures, SourceType,
  useAccountContext,
} from '../../../../contexts';
import { withExpandableRowErrors } from '../../../Errors/ExpandableRowErrors';

const AddAddressContainer = styled(Flex)` ${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  align-items: center;
  gap: ${theme.padding[4]};
  padding: ${theme.padding[1]};
  padding-left: ${theme.padding[4]};
`}`;

const AddAddress = styled.p` ${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  font-weight: ${theme.fontWeight.bold};
  padding-top: ${theme.padding[1]};
`}`;

const ManualAddressContainer = styled(FlexColumn)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  padding-top: ${theme.padding[2]};
  padding-bottom: ${theme.padding[2]};
`}`;

const ManualAddress = styled(Flex)` ${({ theme }) => `
  background: ${theme.colours.neutral[9]};
  border-radius: ${theme.borderRadius[2]};
  padding: ${theme.padding[2]};
  padding-left: ${theme.padding[4]};
`}`;

const ManualAddressOptions = styled(Flex)` ${({ theme }) => `
  padding-bottom: ${theme.padding[1]};
`}`;

export function RentalRIExpandable({
  data,
  rentalIEIndices,
  countries,
  errors,
  update,
}: {
  data: RentalIncomeAndExpenditures,
  rentalIEIndices: Record<string, number>,
  countries: StaticDataItem[],
  errors: Errors,
  update: (field: string, value: any) => Promise<void>,
}) {
  const { credit } = useCreditContext();
  const { addresses = {} } = credit ?? {};

  const { account } = useAccountContext();
  const { rentalIncomeAndExpenditures } = account ?? {};

  const onChangeEquifaxAddress = (address: CreditAddress, checked: boolean) => {
    if (checked) {
      update(`rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].address`, {
        id: address?.id,
        subBuilding: address?.subBuilding,
        houseName: address?.houseName,
        houseNumber: address?.houseNumber,
        street: address?.street1,
        postTown: address?.postTown,
        postCode: address?.postcode,
        country: address?.country,
      } as Address);
    }
  };

  const onSelectAddressSearch = () => {
    update(
      `rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].source`,
      SourceType.AddressSearch,
    );
  };

  const changeAddressSource = (model: ModelType) => {
    update(
      `rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].source`,
      model[`rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].source`],
    );
    update(`rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].address`, null);
  };

  const manualAddresses = useMemo(() => {
    const addedRentalIEAddresses = rentalIncomeAndExpenditures
      ?.filter((rentalIE) => rentalIE?.isAddedToTable)
      ?.map((rentalIE) => rentalIE?.address?.id);

    return Object.values(addresses)
      ?.filter((address) => (!addedRentalIEAddresses?.includes(address?.id) || data?.address?.id === address?.id));
  }, [rentalIncomeAndExpenditures, data, addresses]);

  return (
    <FlexColumn>
      <AddAddressContainer>
        <AddAddress>Add address</AddAddress>
        <ManualAddressOptions>
          <RadioListInput
            id={data?.id}
            field={`rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].source`}
            options={SourceType}
            valueOverride={manualAddresses.length === 0
              ? SourceType.AddressSearch
              : (data?.source || SourceType.Equifax)}
            updateOverride={changeAddressSource}
            labelOverride={{
              'Equifax': 'from Equifax',
              'AddressSearch': 'Address search',
            }}
            disabled={{ 'Equifax': manualAddresses.length === 0 }}
            required
          />
        </ManualAddressOptions>
      </AddAddressContainer>

      {data?.source === SourceType.AddressSearch || manualAddresses.length === 0 ? (
        <AddressSearch
          field={`rentalIncomeAndExpenditures[${rentalIEIndices?.[data?.id]}].address`}
          addressId={data?.address?.id}
          errors={errors}
          countries={countries?.reduce((prev, current) => ({
            ...prev,
            [current?.key]: current?.label,
          }), {} as Record<string, string>)}
          searchPlaceholder="Enter postcode or first line of registered address"
          subBuildingLabel="Flat number"
          buildingNameLabel="House name"
          buildingNumberLabel="House number"
          onSelect={onSelectAddressSearch}
        />
      ) : (
        <ManualAddressContainer>
          {manualAddresses
            ?.map((address) => (
              <ManualAddress key={address?.id}>
                <div>
                  <CheckboxInput
                    id={`${address?.id}-equifax`}
                    value={address?.id === data?.address?.id}
                    onChange={(checked: boolean) => onChangeEquifaxAddress(address, checked)}
                  />
                </div>
                <FlexGrow>
                  {[
                    address?.houseNumber || address?.houseName,
                    address?.street1,
                    address?.street2,
                    address?.postTown,
                    address?.district,
                    address?.postcode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </FlexGrow>
              </ManualAddress>
            ))}
        </ManualAddressContainer>
      )}

    </FlexColumn>
  );
}

export default withExpandableRowErrors(RentalRIExpandable);
